import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import sesionService from '../../services/sesionService';
import especialistaService from '../../services/especialistaService';
import { getEtapasByEspecialidad, getEtapasByHistoria } from '../../services/etapaTratamientoService';
import historiaClinicaService from '../../services/historiaService';

const SesionForm = ({ historiaId, sesionId = null, onSave, onCancel }) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [especialistas, setEspecialistas] = useState([]);
  const [error, setError] = useState(null);
  const isEditing = !!sesionId;
  const [etapas, setEtapas] = useState([]);
  const [historia, setHistoria] = useState(null);
  // Esquema de validación con Yup
  const validationSchema = Yup.object({
    especialista_id: Yup.number()
      .required('El especialista es requerido'),
    fecha: Yup.date()
      .required('La fecha es requerida'),
    hora_inicio: Yup.date()
      .required('La hora de inicio es requerida'),
    hora_fin: Yup.date()
      .required('La hora de fin es requerida'),
    estado: Yup.string()
      .required('El estado es requerido'),
    etapa_id: Yup.number()
      .nullable()
  });

  // Inicializar formik
  const formik = useFormik({
    initialValues: {
      historia_clinica_id: historiaId,
      especialista_id: '',
      etapa_id: null,
      fecha: new Date(),
      hora_inicio: new Date(new Date().setHours(9, 0, 0, 0)),
      hora_fin: new Date(new Date().setHours(10, 0, 0, 0)),
      estado: 'Programada',
      notas: '',
      actividades_realizadas: '',
      avances: '',
      tareas: ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      try {
        // Formatear fecha y horas
        const formattedValues = {
          ...values,
          fecha: format(values.fecha, 'yyyy-MM-dd'),
          hora_inicio: format(values.hora_inicio, 'HH:mm:ss'),
          hora_fin: format(values.hora_fin, 'HH:mm:ss')
        };
        
        let response;
        
        if (isEditing) {
          response = await sesionService.updateSesion(sesionId, formattedValues);
          showNotification('Sesión actualizada correctamente', 'success');
        } else {
          response = await sesionService.createSesion(formattedValues);
          showNotification('Sesión creada correctamente', 'success');
        }
        
        if (onSave && typeof onSave === 'function') {
          onSave(response.data);
        }
      } catch (error) {
        console.error('Error al guardar sesión:', error);
        setError(error.response?.data?.message || `Error al ${isEditing ? 'actualizar' : 'crear'} la sesión`);
        showNotification(`Error al ${isEditing ? 'actualizar' : 'crear'} la sesión`, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchEtapas = async (historiaClinicaId) => {
  try {
    // Obtener las etapas para la historia clínica específica
    const response = await getEtapasByHistoria(historiaClinicaId);
    setEtapas(response.data);
  } catch (error) {
    console.error('Error al cargar etapas:', error);
  }
};

  // Cargar datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
      // Cargar especialistas (código existente)
        const especialistasResponse = await especialistaService.getEspecialistas();
        setEspecialistas(especialistasResponse.data || []);
        
        // Cargar datos de la historia clínica
        const historiaResponse = await historiaClinicaService.getHistoria(historiaId);
        setHistoria(historiaResponse.data);
        
        // Cargar etapas para esta historia
        await fetchEtapas(historiaId);
        
        // Si estamos editando, cargar datos de la sesión
        if (isEditing) {
          const sesionResponse = await sesionService.getSesion(sesionId);
          const sesion = sesionResponse.data;
          
          // Convertir fechas y horas a objetos Date
          const fechaObj = new Date(sesion.fecha);
          
          // Hora inicio
          let horaInicioObj = new Date();
          const [horaInicio, minutosInicio] = sesion.hora_inicio.split(':');
          horaInicioObj.setHours(parseInt(horaInicio), parseInt(minutosInicio), 0, 0);
          
          // Hora fin
          let horaFinObj = new Date();
          const [horaFin, minutosFin] = sesion.hora_fin.split(':');
          horaFinObj.setHours(parseInt(horaFin), parseInt(minutosFin), 0, 0);
          
          // Actualizar formulario
          formik.setValues({
            historia_clinica_id: sesion.historia_clinica_id,
            especialista_id: sesion.especialista_id,
            etapa_id: sesion.etapa_id || null, 
            fecha: fechaObj,
            hora_inicio: horaInicioObj,
            hora_fin: horaFinObj,
            estado: sesion.estado,
            notas: sesion.notas || '',
            actividades_realizadas: sesion.actividades_realizadas || '',
            avances: sesion.avances || '',
            tareas: sesion.tareas || ''
          });
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error al cargar los datos iniciales');
        showNotification('Error al cargar los datos iniciales', 'error');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchInitialData();
  }, [sesionId, historiaId, isEditing]);

  // Verificar si es sesión realizada
  const isSesionRealizada = formik.values.estado === 'Realizada';

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {isEditing ? 'Editar Sesión' : 'Nueva Sesión'}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Datos básicos */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={formik.touched.especialista_id && Boolean(formik.errors.especialista_id)}
              >
                <InputLabel id="especialista-label">Especialista *</InputLabel>
                <Select
                  labelId="especialista-label"
                  id="especialista_id"
                  name="especialista_id"
                  value={formik.values.especialista_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Especialista *"
                >
                  {especialistas.map(esp => (
                    <MenuItem key={esp.id} value={esp.id}>
                      {esp.usuario?.nombre} {esp.usuario?.apellidos}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.especialista_id && formik.errors.especialista_id && (
                  <FormHelperText>{formik.errors.especialista_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={formik.touched.etapa_id && Boolean(formik.errors.etapa_id)}
              >
                <InputLabel id="etapa-label">Etapa de Tratamiento</InputLabel>
                <Select
                  labelId="etapa-label"
                  id="etapa_id"
                  name="etapa_id"
                  value={formik.values.etapa_id || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Etapa de Tratamiento"
                >
                  <MenuItem value="">Ninguna específica</MenuItem>
                  {etapas.map(etapa => (
                    <MenuItem key={etapa.id} value={etapa.id}>
                      {etapa.nombre}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.etapa_id && formik.errors.etapa_id && (
                  <FormHelperText>{formik.errors.etapa_id}</FormHelperText>
                )}
                <FormHelperText>
                  La etapa de tratamiento indica en qué fase se encuentra el paciente
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={formik.touched.estado && Boolean(formik.errors.estado)}
              >
                <InputLabel id="estado-label">Estado *</InputLabel>
                <Select
                  labelId="estado-label"
                  id="estado"
                  name="estado"
                  value={formik.values.estado}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Estado *"
                >
                  <MenuItem value="Programada">Programada</MenuItem>
                  <MenuItem value="Realizada">Realizada</MenuItem>
                  <MenuItem value="Cancelada">Cancelada</MenuItem>
                  <MenuItem value="No asistió">No asistió</MenuItem>
                </Select>
                {formik.touched.estado && formik.errors.estado && (
                  <FormHelperText>{formik.errors.estado}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Fecha *"
                value={formik.values.fecha}
                onChange={(date) => formik.setFieldValue('fecha', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.fecha && Boolean(formik.errors.fecha),
                    helperText: formik.touched.fecha && formik.errors.fecha
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TimePicker
                label="Hora inicio *"
                value={formik.values.hora_inicio}
                onChange={(time) => formik.setFieldValue('hora_inicio', time)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.hora_inicio && Boolean(formik.errors.hora_inicio),
                    helperText: formik.touched.hora_inicio && formik.errors.hora_inicio
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TimePicker
                label="Hora fin *"
                value={formik.values.hora_fin}
                onChange={(time) => formik.setFieldValue('hora_fin', time)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.hora_fin && Boolean(formik.errors.hora_fin),
                    helperText: formik.touched.hora_fin && formik.errors.hora_fin
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notas"
                name="notas"
                label="Notas"
                multiline
                rows={2}
                value={formik.values.notas}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>
            
            {/* Campos adicionales para sesiones realizadas */}
            {isSesionRealizada && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Detalles de la Sesión Realizada
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="actividades_realizadas"
                    name="actividades_realizadas"
                    label="Actividades realizadas"
                    multiline
                    rows={3}
                    value={formik.values.actividades_realizadas}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="avances"
                    name="avances"
                    label="Avances observados"
                    multiline
                    rows={3}
                    value={formik.values.avances}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="tareas"
                    name="tareas"
                    label="Tareas asignadas"
                    multiline
                    rows={3}
                    value={formik.values.tareas}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
              </>
            )}
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="button"
              onClick={onCancel} 
              sx={{ mr: 2 }}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Guardar'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default SesionForm;